/**
 * AutoMazen FAQ Section JavaScript
 * 
 * Features:
 * - Accessible accordion functionality
 * - Keyboard navigation support
 * - ARIA attribute management
 * - Smooth animations
 */

// Initialize FAQ functionality with a self-executing function to avoid global scope pollution
(function() {
  const selectors = {
    section: '[data-section-type="faq"]',
    faqItem: '.automazen-faq__item',
    faqQuestion: '.automazen-faq__question',
    faqAnswer: '.automazen-faq__answer'
  };

  class AutoMazenFAQ {
    constructor(container) {
      this.container = container;
      this.sectionId = container.dataset.sectionId;
      this.faqItems = this.container.querySelectorAll(selectors.faqItem);
      
      this.init();
    }

    init() {
      // Initialize accordion functionality
      this.initAccordion();
      
      // Initialize CSS variables from section settings
      this.initCssVariables();
    }

    initCssVariables() {
      // Get theme settings from data attributes or CSS variables
      const style = getComputedStyle(this.container);
      const isMobile = window.innerWidth < 768;
      
      // Process background colors and text colors
      const backgroundColor = this.container.dataset.backgroundColor || '#f9f9f9';
      const textColor = this.container.dataset.textColor || '#333333';
      const accentColor = this.container.dataset.accentColor || '#98CA3F';
      
      // Set base colors
      this.container.style.setProperty('--background-color', backgroundColor);
      this.container.style.setProperty('--text-color', textColor);
      this.container.style.setProperty('--accent-color', accentColor);
      this.container.style.setProperty('--subtitle-color', this.hexToRgba(textColor, 0.8));
      
      // Process accent color for RGB components
      const accentColorRgb = this.hexToRgb(accentColor);
      if (accentColorRgb) {
        this.container.style.setProperty('--accent-color-rgb', `${accentColorRgb.r}, ${accentColorRgb.g}, ${accentColorRgb.b}`);
      }
      
      // Process question and answer colors
      const questionBgColor = this.container.dataset.questionBackgroundColor || '#ffffff';
      const questionTextColor = this.container.dataset.questionTextColor || '#333333';
      const questionHoverBgColor = this.container.dataset.questionHoverBackgroundColor || '#f0f0f0';
      const questionHoverTextColor = this.container.dataset.questionHoverTextColor || '#000000';
      const answerBgColor = this.container.dataset.answerBackgroundColor || '#98CA3F';
      const answerTextColor = this.container.dataset.answerTextColor || '#ffffff';
      
      // Apply question and answer colors
      this.container.style.setProperty('--question-background-color', questionBgColor);
      this.container.style.setProperty('--question-text-color', questionTextColor);
      this.container.style.setProperty('--question-hover-background-color', questionHoverBgColor);
      this.container.style.setProperty('--question-hover-text-color', questionHoverTextColor);
      this.container.style.setProperty('--answer-background-color', answerBgColor);
      this.container.style.setProperty('--answer-text-color', answerTextColor);
      
      // Apply responsive padding - Desktop vs Mobile
      const paddingTop = isMobile 
        ? parseInt(this.container.dataset.mobilePaddingTop || '50')
        : parseInt(this.container.dataset.paddingTop || '80');
      const paddingBottom = isMobile 
        ? parseInt(this.container.dataset.mobilePaddingBottom || '50')
        : parseInt(this.container.dataset.paddingBottom || '80');
        
      this.container.style.setProperty('--padding-top', `${paddingTop}px`);
      this.container.style.setProperty('--padding-bottom', `${paddingBottom}px`);
      
      // Set both desktop and mobile font sizes
      const desktopTitleSize = parseInt(this.container.dataset.titleSize || '40');
      const desktopSubtitleSize = parseInt(this.container.dataset.subtitleSize || '18');
      const desktopQuestionSize = parseInt(this.container.dataset.questionSize || '20');
      const desktopAnswerSize = parseInt(this.container.dataset.answerSize || '16');
      
      const mobileTitleSize = parseInt(this.container.dataset.mobileTitleSize || '28');
      const mobileSubtitleSize = parseInt(this.container.dataset.mobileSubtitleSize || '16');
      const mobileQuestionSize = parseInt(this.container.dataset.mobileQuestionSize || '18');
      const mobileAnswerSize = parseInt(this.container.dataset.mobileAnswerSize || '14');
      
      // Apply font sizes
      this.container.style.setProperty('--title-font-size', `${desktopTitleSize}px`);
      this.container.style.setProperty('--subtitle-font-size', `${desktopSubtitleSize}px`);
      this.container.style.setProperty('--question-font-size', `${desktopQuestionSize}px`);
      this.container.style.setProperty('--answer-font-size', `${desktopAnswerSize}px`);
      
      // Always set mobile sizes
      this.container.style.setProperty('--mobile-title-font-size', `${mobileTitleSize}px`);
      this.container.style.setProperty('--mobile-subtitle-font-size', `${mobileSubtitleSize}px`);
      this.container.style.setProperty('--mobile-question-font-size', `${mobileQuestionSize}px`);
      this.container.style.setProperty('--mobile-answer-font-size', `${mobileAnswerSize}px`);
      
      // Store mobile sizes - these will be used by the resize handler
      this.container.dataset.mobileTitleFontSize = this.container.dataset.mobileTitleSize || '28';
      this.container.dataset.mobileSubtitleFontSize = this.container.dataset.mobileSubtitleSize || '16';
      this.container.dataset.mobileQuestionFontSize = this.container.dataset.mobileQuestionSize || '18';
      this.container.dataset.mobileAnswerFontSize = this.container.dataset.mobileAnswerSize || '14';
      this.container.dataset.mobilePaddingTop = this.container.dataset.mobilePaddingTop || '50';
      this.container.dataset.mobilePaddingBottom = this.container.dataset.mobilePaddingBottom || '50';
      
      // Store desktop sizes
      this.container.dataset.desktopTitleFontSize = this.container.dataset.titleSize || '40';
      this.container.dataset.desktopSubtitleFontSize = this.container.dataset.subtitleSize || '18';
      this.container.dataset.desktopQuestionFontSize = this.container.dataset.questionSize || '20';
      this.container.dataset.desktopAnswerFontSize = this.container.dataset.answerSize || '16';
      this.container.dataset.desktopPaddingTop = this.container.dataset.paddingTop || '80';
      this.container.dataset.desktopPaddingBottom = this.container.dataset.paddingBottom || '80';
      
      // Add window resize listener to handle responsive behavior
      this.setupResponsiveListener();
    }

    hexToRgb(hex) {
      // Convert hex color to rgb object
      if (!hex) return null;
      
      // Remove # if present
      hex = hex.replace('#', '');
      
      // Convert 3-digit hex to 6-digit
      if (hex.length === 3) {
        hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
      }
      
      // Convert hex to rgb
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      
      return { r, g, b };
    }
    
    hexToRgba(hex, alpha) {
      // Convert hex color to rgba
      if (!hex) return `rgba(51, 51, 51, ${alpha})`;
      
      const rgb = this.hexToRgb(hex);
      if (!rgb) return `rgba(51, 51, 51, ${alpha})`;
      
      return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
    }

    setupResponsiveListener() {
      // Debounce function to limit resize event firing
      let resizeTimeout;
      
      // Store reference to this for use in event handler
      const self = this;
      
      // Add resize event listener to window
      window.addEventListener('resize', function() {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(function() {
          const isMobile = window.innerWidth < 768;
          
          // Update font sizes based on screen size
          if (isMobile) {
            self.container.style.setProperty('--title-font-size', `${self.container.dataset.mobileTitleFontSize}px`);
            self.container.style.setProperty('--subtitle-font-size', `${self.container.dataset.mobileSubtitleFontSize}px`);
            self.container.style.setProperty('--question-font-size', `${self.container.dataset.mobileQuestionFontSize}px`);
            self.container.style.setProperty('--answer-font-size', `${self.container.dataset.mobileAnswerFontSize}px`);
            self.container.style.setProperty('--padding-top', `${self.container.dataset.mobilePaddingTop}px`);
            self.container.style.setProperty('--padding-bottom', `${self.container.dataset.mobilePaddingBottom}px`);
          } else {
            self.container.style.setProperty('--title-font-size', `${self.container.dataset.desktopTitleFontSize}px`);
            self.container.style.setProperty('--subtitle-font-size', `${self.container.dataset.desktopSubtitleFontSize}px`);
            self.container.style.setProperty('--question-font-size', `${self.container.dataset.desktopQuestionFontSize}px`);
            self.container.style.setProperty('--answer-font-size', `${self.container.dataset.desktopAnswerFontSize}px`);
            self.container.style.setProperty('--padding-top', `${self.container.dataset.desktopPaddingTop}px`);
            self.container.style.setProperty('--padding-bottom', `${self.container.dataset.desktopPaddingBottom}px`);
          }
        }, 250);
      });
    }
    
    initAccordion() {
      // Add click event listeners to all questions
      this.faqItems.forEach((item) => {
        const question = item.querySelector(selectors.faqQuestion);
        const answer = item.querySelector(selectors.faqAnswer);
        
        if (!question || !answer) return;
        
        // Add click event listener
        question.addEventListener('click', (event) => {
          this.toggleAccordion(question, answer);
        });
        
        // Add keyboard event listener
        question.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.toggleAccordion(question, answer);
          }
        });
      });
    }

    toggleAccordion(question, answer) {
      const isExpanded = question.getAttribute('aria-expanded') === 'true';
      
      // Toggle ARIA attributes
      question.setAttribute('aria-expanded', !isExpanded);
      answer.setAttribute('aria-hidden', isExpanded);
      
      // Toggle max-height for smooth animation
      if (isExpanded) {
        answer.style.maxHeight = '0px';
      } else {
        // Set max-height to scrollHeight for smooth animation
        answer.style.maxHeight = answer.scrollHeight + 'px';
      }
    }
  }

  // Initialize all FAQ sections on the page
  function initFAQSections() {
    const faqSections = document.querySelectorAll(selectors.section);
    
    faqSections.forEach((section) => {
      new AutoMazenFAQ(section);
    });
  }

  // Initialize on DOM content loaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFAQSections);
  } else {
    initFAQSections();
  }

  // Handle Shopify section load events for the theme editor
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector(selectors.section);
    if (section) {
      new AutoMazenFAQ(section);
    }
  });
})();
